import React, { FC, Suspense, useEffect, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchQuery } from 'relay-runtime';
import { backofficeUrl, T, toGlobalID, toLocalID } from 'src/shared/helpers';
import { default as CogIcon, default as SettingsIcon } from 'src/shared/material_icons/action/settings.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import LinkOffIcon from 'src/shared/material_icons/content/link_off.svg';
import VerticalThreePoint from 'src/shared/material_icons/navigation/more_vert.svg';
import styled from 'styled-components';
import { Badge } from '../../widgets/badge';
import { BasicButton, GhostButton, Link, PrimaryButton, TextButton } from '../../widgets/buttons';
import { CollapsibleSection } from '../../widgets/collapsibleSection';
import { DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { DropdownActionItem, DropdownActions } from '../../widgets/dropdown';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import { InlineHelpLayout } from '../../widgets/inlineHelpLayout';
import { ContainerBase } from '../../widgets/layouts';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { PageHeader } from '../../widgets/pageHeader';
import { Section } from '../../widgets/section';
import { SectionContent } from '../../widgets/section/sectionContent';
import { Spinner } from '../../widgets/spinner';
import Stack from '../../widgets/stack/stack';
import { Row, Table, TableHeading } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';
import SalesChannelIcon from '../products/salesChannelIcon';
import { salesChannelConfirmShopifyChargeMutation } from './__generated__/salesChannelConfirmShopifyChargeMutation.graphql';
import { salesChannelCreatePaymentConfirmedTaskMutation } from './__generated__/salesChannelCreatePaymentConfirmedTaskMutation.graphql';
import { salesChannelGenerateDeleteShopifyRecurringChargesTaskMutation } from './__generated__/salesChannelGenerateDeleteShopifyRecurringChargesTaskMutation.graphql';
import { salesChannelGenerateEditSubscriptionTaskMutation } from './__generated__/salesChannelGenerateEditSubscriptionTaskMutation.graphql';
import { salesChannelGenerateProductSwitcherTaskMutation } from './__generated__/salesChannelGenerateProductSwitcherTaskMutation.graphql';
// TASK_TYPE_SWITCH_INTEGRATION_CONTEXT / TASK_TYPE_REVERT_INTEGRATION_CONTEXT are no longer used in the Shopify switch flow — commented out for now.
// import { salesChannelGenerateRevertIntegrationContextTaskMutation } from './__generated__/salesChannelGenerateRevertIntegrationContextTaskMutation.graphql';
// import { salesChannelGenerateSwitchIntegrationContextTaskMutation } from './__generated__/salesChannelGenerateSwitchIntegrationContextTaskMutation.graphql';
import { renameSalesChannelDialogMutation } from './__generated__/renameSalesChannelDialogMutation.graphql';
import { salesChannelPollUnsubscribeTaskQuery } from './__generated__/salesChannelPollUnsubscribeTaskQuery.graphql';
import { salesChannelProductsOnDefaultQuery } from './__generated__/salesChannelProductsOnDefaultQuery.graphql';
import { salesChannelRemoveIntegrationMutation } from './__generated__/salesChannelRemoveIntegrationMutation.graphql';
import { ECommerce, salesChannelsActive$data, salesChannelsActive$key } from './__generated__/salesChannelsActive.graphql';
import { salesChannelSetStatusMutation } from './__generated__/salesChannelSetStatusMutation.graphql';
import { salesChannelsQuery } from './__generated__/salesChannelsQuery.graphql';

import { useRelayEnvironment } from 'react-relay';
import Loader from 'src/components/widgets/loader';
import { useAppState } from 'src/shared/appState';
import config from 'src/shared/config';
import { useFeatureFlag, useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { Center } from '../../../shared/globals';
import CreateStandaloneSiteDialog from '../settings/standaloneSites/createStandaloneSiteDialog';
import { getStandaloneSitePublicUrl } from '../settings/standaloneSites/standaloneSiteNavigation';
import { salesChannelCheckSellerPlanQuery } from './__generated__/salesChannelCheckSellerPlanQuery.graphql';
import { salesChannelCheckShopifyTestStoreQuery } from './__generated__/salesChannelCheckShopifyTestStoreQuery.graphql';
import { salesChannelDeleteMiniSiteMutation } from './__generated__/salesChannelDeleteMiniSiteMutation.graphql';
import { salesChannelMiniSitesQuery } from './__generated__/salesChannelMiniSitesQuery.graphql';
import { salesChannelRenameMiniSiteMutation } from './__generated__/salesChannelRenameMiniSiteMutation.graphql';
import AddSalesChannelDialog from './addSalesChannelDialog';
import ApiIntegrationSalesChannelDialog from './apiIntegrationSalesChannelDialog';
import ApiKeyDialog from './apiKeyDialog';
import CCloudIntegrationSalesChannelDialog from './cCloudIntegrationSalesChannelDialog';
import { generateStoreNumbers } from './helpers';
import InlineRenameCell from './inlineRenameCell';
import PaymentSuccessDialog from './paymentSuccessDialog';
import { updateNameSalesChannel } from './renameSalesChannelDialog';
import StoreUrlSalesChannelDialog from './storeUrlSalesChannelDialog';
import SwitchSalesChannelDialog, { SellerPlanCheckInfo, TASKSTATUS } from './switchSalesChannelDialog';

const StyledImg = styled.div`
    > div {
        width: 32px;
        height: 32px;
    }

    img {
        width: 30px;
        height: 30px;
    }
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const StyledLinkContainer = styled.div`
    word-wrap: nowrap;
`;

const StyledDivElementTable = styled.div`
    align-content: center;
    max-width: 100%;
`;

const StyledLink = styled(Link)`
    font-weight: 400;

    span {
        word-break: break-all;
    }
`;

const StoreNumberCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionHeaderActionsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const HeaderActionsDivider = styled.div`
    width: 1px;
    align-self: stretch;
    min-height: 24px;
    background: ${(props) => props.theme.app.borderColor};
`;

// Tighten the gap between the section header/description and the table below it.
const CompactSection = styled(Section)`
    ${SectionContent} {
        padding-top: 8px;
    }
`;

// Pull the empty state up so it stays visible even when the online stores table above is tall.
const CompactEmptyState = styled.div`
    > div {
        padding-top: 8px;
    }
`;

// Shared column template so the Online Stores and Mini-Sites tables line up column-for-column.
const SALES_CHANNEL_TABLE_WIDTHS = ['0.85fr', '1.21fr', 'minmax(auto, 500px)', '0.6fr', '1.14fr'];

const salesChannelMiniSitesQueryNode = graphql`
    query salesChannelMiniSitesQuery {
        standaloneSites {
            id
            standaloneSiteID
            name
            domain
            status
        }
    }
`;

const salesChannelRenameMiniSiteMutationNode = graphql`
    mutation salesChannelRenameMiniSiteMutation($input: StandaloneSiteUpdateInput!) {
        updateStandaloneSite(input: $input) {
            id
            name
        }
    }
`;

const salesChannelDeleteMiniSiteMutationNode = graphql`
    mutation salesChannelDeleteMiniSiteMutation($id: ID!) {
        deleteStandaloneSite(id: $id)
    }
`;

const renderMiniSiteStatusBadge = (status: string) => {
    switch (status) {
        case 'ACTIVE':
            return <Badge variant='success'>{T._('Active', 'Admin')}</Badge>;
        case 'DRAFT':
            return <Badge variant='warning'>{T._('Draft', 'Admin')}</Badge>;
        case 'DISABLED':
            return <Badge variant='danger'>{T._('Disabled', 'Admin')}</Badge>;
        default:
            return <Badge variant='neutral'>{status}</Badge>;
    }
};

const MiniSitesList: FC<{ fetchKey: number; onChanged: () => void; onAdd: () => void }> = ({ fetchKey, onChanged, onAdd }) => {
    const navigate = useNavigate();
    const { showDialog, closeDialog } = useDialogManager();
    const data = useLazyLoadQuery<salesChannelMiniSitesQuery>(salesChannelMiniSitesQueryNode, {}, { fetchKey, fetchPolicy: 'network-only' });
    const sites = data.standaloneSites || [];

    const [renameSite] = useMutation<salesChannelRenameMiniSiteMutation>(salesChannelRenameMiniSiteMutationNode);
    const [deleteSite] = useMutation<salesChannelDeleteMiniSiteMutation>(salesChannelDeleteMiniSiteMutationNode);

    const handleRename = (id: string, newName: string) => {
        renameSite({
            variables: { input: { id, name: newName } },
            onError: () => {
                showErrorNotification({ message: T._('Failed to rename mini-site.', 'Admin') });
            },
            onCompleted: () => {}
        });
    };

    const goToSettings = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/settings`));
    const goToTheme = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/themes`));

    const handleDelete = (site: { id: string; name: string }) => {
        showDialog(
            'delete-mini-site',
            <QuestionDialog
                showCloseButton={false}
                title={T._('Delete mini-site', 'Admin')}
                message={T._(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`, 'Admin')}
                buttonYesLabel={T._('Cancel', 'Admin')}
                buttonNoLabel={T._('Delete', 'Admin')}
                destructiveNo
                onYesClick={() => closeDialog('delete-mini-site')}
                onNoClick={() => {
                    closeDialog('delete-mini-site');
                    deleteSite({
                        variables: { id: site.id },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Mini-site deleted.', 'Admin') });
                            onChanged();
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed to delete mini-site.', 'Admin') });
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    if (sites.length === 0) {
        return (
            <CompactSection title={T._('Mini-Sites', 'Admin')}>
                <CompactEmptyState>
                    <EmptyList
                        hideIcon
                        title={''}
                        subTitle={T._(
                            'Create a branded mini-site powered by Zakeke, accessible directly on the web\nor embeddable in a physical kiosk, POS or similar solutions.',
                            'Admin'
                        )}
                        learnMoreText={T._('Learn more', 'Admin')}
                        learnMoreLink='https://zakeke.zendesk.com/hc/en-us'
                        buttonTitle={T._('Add', 'Admin')}
                        onButtonClick={onAdd}
                    />
                </CompactEmptyState>
            </CompactSection>
        );
    }

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Status', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    return (
        <CompactSection
            title={T._('Mini-Sites', 'Admin')}
            description={T._(
                'Launch standalone customizer portals for B2B sales, kiosks, showrooms, POS, and other experiences that do not require Zakeke embedded in an online store.',
                'Admin'
            )}
            headerActions={
                <PrimaryButton $size='medium' onClick={onAdd} style={{ minWidth: '160px' }}>
                    <Icon>
                        <PlusIcon />
                    </Icon>
                    {T._('Add Mini-Site', 'Admin')}
                </PrimaryButton>
            }
        >
            <Table headings={headings} compact>
                {sites.map((site, i) => {
                    const publicUrl = getStandaloneSitePublicUrl(site.domain);
                    return (
                        <Row
                            key={site.id}
                            cells={[
                                <StyledDivElementTable>{T._(`Mini-Site ${i + 1}`, 'Admin')}</StyledDivElementTable>,
                                <InlineRenameCell name={site.name} onRename={(newName) => handleRename(site.id, newName)} />,
                                <StyledDivElementTable>
                                    {publicUrl ? (
                                        <StyledLinkContainer>
                                            <StyledLink href={publicUrl} target='_blank' title={publicUrl}>
                                                <span>{publicUrl}</span>
                                            </StyledLink>
                                        </StyledLinkContainer>
                                    ) : (
                                        '-'
                                    )}
                                </StyledDivElementTable>,
                                <StyledDivElementTable>{renderMiniSiteStatusBadge(site.status)}</StyledDivElementTable>,
                                <ActionsColContainer>
                                    <ZTooltip position={'top'} title={T._('Settings', 'Admin')}>
                                        <GhostButton
                                            $size='small'
                                            $type='icon'
                                            aria-label={T._('Settings', 'Admin')}
                                            onClick={() => goToSettings(site.standaloneSiteID.toString())}
                                        >
                                            <Icon>
                                                <SettingsIcon />
                                            </Icon>
                                        </GhostButton>
                                    </ZTooltip>
                                    <DropdownActions
                                        header={
                                            <Icon $hoverable>
                                                <VerticalThreePoint />
                                            </Icon>
                                        }
                                        orientation={'RIGHT'}
                                        sections={[
                                            [
                                                <DropdownActionItem
                                                    key='view'
                                                    disabled={!publicUrl}
                                                    onClick={() => publicUrl && window.open(publicUrl, '_blank')}
                                                >
                                                    {T._('View mini-site', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='settings' onClick={() => goToSettings(site.standaloneSiteID.toString())}>
                                                    {T._('Settings', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='theme' onClick={() => goToTheme(site.standaloneSiteID.toString())}>
                                                    {T._('Manage theme', 'Admin')}
                                                </DropdownActionItem>
                                            ],
                                            [
                                                <DropdownActionItem key='delete' danger onClick={() => handleDelete(site)}>
                                                    {T._('Delete', 'Admin')}
                                                </DropdownActionItem>
                                            ]
                                        ]}
                                    />
                                </ActionsColContainer>
                            ]}
                        />
                    );
                })}
            </Table>
        </CompactSection>
    );
};

const UnclickableDisconnectButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const SalesChannelNotAvailableDialogWindow = styled(DialogWindow)`
    p {
        font-size: 14px;
    }

    max-width: 600px;
`;

const StyledContainerBase = styled(ContainerBase)<{ showBackButton: boolean }>`
    padding-top: ${(props) => (props?.showBackButton ? '0' : '60px')};
`;

const HeroSection = styled.div`
    text-align: center;
    padding: 48px 24px 40px;
`;

const HeroTitle = styled.h2`
    font-size: 22px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 12px;
`;

const HeroDescription = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 auto;
    max-width: 520px;
    line-height: 1.6;
`;

const ChannelCardsGrid = styled.div<{ $singleColumn?: boolean }>`
    display: grid;
    grid-template-columns: ${(props) => (props.$singleColumn ? 'minmax(0, 450px)' : '1fr 1fr')};
    justify-content: center;
    gap: 24px;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ChannelCard = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 28px;
    position: relative;
    background: ${(props) => props.theme.app.backgroundColor};
    display: flex;
    flex-direction: column;
`;

const CardBadge = styled.span<{ $variant: 'store' | 'portal' }>`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${(props) => (props.$variant === 'store' ? 'rgba(232, 101, 10, 0.1)' : 'rgba(2, 136, 209, 0.1)')};
    color: ${(props) => (props.$variant === 'store' ? props.theme.app.secondary : props.theme.app.secondary_alt)};
    align-self: flex-start;
`;

const CardTitle = styled.h3`
    font-size: 18px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 16px 0 8px;
`;

const CardText = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 0 20px;
    line-height: 1.5;
    /* Keep the description height consistent so both feature lists start at the same height */
    min-height: 84px;

    @media (max-width: 768px) {
        min-height: 0;
    }
`;

const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
`;

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    padding: 4px 0;

    &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${(props) => props.theme.app.component.notificationBanner.success.iconContainerBGColor};
        flex-shrink: 0;
    }
`;

const CardAction = styled.div`
    margin-top: auto;
`;

const FullWidthPrimaryButton = styled(PrimaryButton)`
    width: 100%;
`;

const InfoBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 16px 24px;
    /* Matches the cards row width above (grid max-width 900px minus its 24px side padding) */
    max-width: 852px;
    margin: 32px auto 0;
    gap: 16px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const InfoBannerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const InfoIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(2, 136, 209, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${(props) => props.theme.app.secondary_alt};
    font-size: 16px;
    font-weight: ${(props) => props.theme.font.semi_bold};
`;

const InfoBannerTextBlock = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.app.primary};
`;

export const activeSalesChannelFragment = graphql`
    fragment salesChannelsActive on Query @refetchable(queryName: "salesChannelActiveFragmentQuery") {
        user {
            userID
            email
        }
        salesChannels(status: ACTIVE) {
            id
            detail {
                id
                properties {
                    name
                }
            }
            ecommerceSettings {
                id
                ecommerceGlobalID
                ecommerceName
                remoteUrl
                isMarketplace
                definitiveUrl
                currencyCode
            }
        }
        productSwitcherTasks {
            taskID
            state
            taskType
            lastError
            elapsedTime
            modifiedOn
            extraData {
                taskID
                masterSalesChannelID
                secondarySalesChannelID
                delayMinutes
            }
        }
        isSellerMigrating
    }
`;

const DisabledSalesChannelContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    cursor: not-allowed;
`;

const InfoText = styled.div`
    font-size: 12px;
    color: #78909c;
`;

const ProductItem = styled.div`
    padding: 4px 0;
    font-size: 13px;
    color: #374151;
`;

const RemainingProductsSection = styled.div`
    margin-top: 8px;

    > div {
        border-bottom: none;
    }
`;

const INLINE_PRODUCTS_LIMIT = 5;

const DefaultSalesChannelProductsList: FC = () => {
    const data = useLazyLoadQuery<salesChannelProductsOnDefaultQuery>(
        graphql`
            query salesChannelProductsOnDefaultQuery {
                productsOnDefaultSalesChannel {
                    id
                    code
                    name
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );

    const products = data.productsOnDefaultSalesChannel;
    if (products.length === 0) return null;

    const inlineProducts = products.slice(0, INLINE_PRODUCTS_LIMIT);
    const remainingProducts = products.slice(INLINE_PRODUCTS_LIMIT);
    const hasMore = remainingProducts.length > 0;

    return (
        <div>
            <ul>
                {inlineProducts.map((product) => (
                    <li key={product.id}>
                        {product.name} ({product.code})
                    </li>
                ))}
            </ul>
            {hasMore && (
                <RemainingProductsSection>
                    <CollapsibleSection title={T._('More affected products', 'Admin')} count={remainingProducts.length}>
                        {remainingProducts.map((product) => (
                            <ProductItem key={product.id}>
                                {product.name} ({product.code})
                            </ProductItem>
                        ))}
                    </CollapsibleSection>
                </RemainingProductsSection>
            )}
        </div>
    );
};

const UNSUBSCRIBE_TASK_STORAGE_KEY = 'unsubscribingPlanTask';
const SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY = 'switchSubscriptionReturn';

// Stages of the sales channel switch that are polled via startPollingTask (persisted so the polling dialog can resume after a page refresh)
type SwitchPollingFlowType = 'shopify' | 'nonShopify' | 'switchIntegrationContext' | 'deleteRecurringCharges';

const HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels';

const SalesChannels: FC<{}> = () => {
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    const [miniSitesFetchKey, setMiniSitesFetchKey] = useState(0);
    const [checkingSellerPlanForChannelID, setCheckingSellerPlanForChannelID] = useState<string | null>(null);
    const { showDialog, closeDialog } = useDialogManager();
    const [isPending, startTransition] = useTransition();
    const { state } = useAppState();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const added = searchParams.get('added') || 'false';
    const isMaster = searchParams.get('isMaster') || 'false';

    // Payment return parameters
    const paymentSuccess = searchParams.get('paymentSuccess');
    const salesChannelIDParam = searchParams.get('salesChannelID');
    const masterSalesChannelIDParam = searchParams.get('masterSalesChannelID');
    const chargeId = searchParams.get('charge_id');
    // Set by the backend when returning from the Shopify plan re-approval during a sales-channel switch (see handleRevertAndRedirectToShopify)
    const switchSubscriptionApproved = searchParams.get('switchSubscriptionApproved');

    const query = useLazyLoadQuery<salesChannelsQuery>(
        graphql`
            query salesChannelsQuery {
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                ...salesChannelsActive
                subscription {
                    areSalesChannelsEnabled
                    planId
                }
                standaloneSites {
                    standaloneSiteID
                }
            }
        `,
        {},
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const [setSalesChannelStatus, isSetSalesChannelStatusPending] = useMutation<salesChannelSetStatusMutation>(graphql`
        mutation salesChannelSetStatusMutation($id: ID!, $status: SalesChannelStatus!) {
            setSalesChannelStatus(id: $id, status: $status) {
                id
            }
        }
    `);

    const [inlineRenameSalesChannel] = useMutation<renameSalesChannelDialogMutation>(updateNameSalesChannel);

    const [removeMasterIntegration] = useMutation<salesChannelRemoveIntegrationMutation>(graphql`
        mutation salesChannelRemoveIntegrationMutation($salesChannelID: ID) {
            removeEcommerceSettings(salesChannelID: $salesChannelID)
        }
    `);

    const [confirmShopifyCharge] = useMutation<salesChannelConfirmShopifyChargeMutation>(graphql`
        mutation salesChannelConfirmShopifyChargeMutation($input: ShopifyChargeConfirmationInput!) {
            confirmShopifyCharge(input: $input)
        }
    `);

    const [generateUnsubscribeTask] = useMutation<salesChannelGenerateEditSubscriptionTaskMutation>(graphql`
        mutation salesChannelGenerateEditSubscriptionTaskMutation($input: EditSubscriptionTaskInput!) {
            generateEditSubscriptionTask(input: $input) {
                taskID
            }
        }
    `);

    const [generateProductSwitcherTask] = useMutation<salesChannelGenerateProductSwitcherTaskMutation>(graphql`
        mutation salesChannelGenerateProductSwitcherTaskMutation($input: ProductSwitcherTaskInput!) {
            generateProductSwitcherTask(input: $input) {
                taskID
            }
        }
    `);

    // TASK_TYPE_SWITCH_INTEGRATION_CONTEXT / TASK_TYPE_REVERT_INTEGRATION_CONTEXT are no longer used — commented out for now.
    /*
    const [generateSwitchIntegrationContextTask] = useMutation<salesChannelGenerateSwitchIntegrationContextTaskMutation>(graphql`
        mutation salesChannelGenerateSwitchIntegrationContextTaskMutation($input: SwitchIntegrationContextTaskInput!) {
            generateSwitchIntegrationContextTask(input: $input) {
                taskID
            }
        }
    `);

    const [generateRevertIntegrationContextTask] = useMutation<salesChannelGenerateRevertIntegrationContextTaskMutation>(graphql`
        mutation salesChannelGenerateRevertIntegrationContextTaskMutation($input: RevertIntegrationContextTaskInput!) {
            generateRevertIntegrationContextTask(input: $input) {
                taskID
            }
        }
    `);
    */

    const [generateDeleteShopifyRecurringChargesTask] = useMutation<salesChannelGenerateDeleteShopifyRecurringChargesTaskMutation>(graphql`
        mutation salesChannelGenerateDeleteShopifyRecurringChargesTaskMutation($input: DeleteShopifyRecurringChargesTaskInput!) {
            generateDeleteShopifyRecurringChargesTask(input: $input) {
                taskID
            }
        }
    `);

    const [createPaymentConfirmedTask] = useMutation<salesChannelCreatePaymentConfirmedTaskMutation>(graphql`
        mutation salesChannelCreatePaymentConfirmedTaskMutation($input: PaymentConfirmedTaskInput!) {
            generatePaymentConfirmedTask(input: $input) {
                taskID
            }
        }
    `);

    const pollUnsubscribeTaskQuery = graphql`
        query salesChannelPollUnsubscribeTaskQuery($id: ID!) {
            productSwitcherTask(id: $id) {
                id
                taskID
                state
                lastError
            }
        }
    `;

    const checkSellerPlanQuery = graphql`
        query salesChannelCheckSellerPlanQuery {
            checkSellerPlan {
                isLegacyPlan
                isV1Plan
                isProductLimitReached
                maxProductsGrow
                maxProductsScale
                currentProductCount
                canSubscribeToNewPlan
            }
            modelsCount(environment: 2)
            hasPodProducts
        }
    `;

    const checkShopifyTestStoreQuery = graphql`
        query salesChannelCheckShopifyTestStoreQuery($salesChannelID: Long) {
            shopifyAuthEcommerceSettings(salesChannelID: $salesChannelID) {
                isTestStore
            }
        }
    `;

    const Environment = useRelayEnvironment();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [activeSalesChannels, refetch] = useRefetchableFragment<salesChannelsQuery, salesChannelsActive$key>(activeSalesChannelFragment, query);

    const isStandaloneEnabled = useFeatureFlag('standalone');

    const isSwitchSalesChannelAvailable = useFeatureFlagWithConditions('switchSalesChannel', {
        userId: activeSalesChannels.user.userID.toString()
    });

    const masterSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) == 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const otherSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) != 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const productSwitcherTasks = activeSalesChannels.productSwitcherTasks || [];
    const isSellerMigrating = activeSalesChannels.isSellerMigrating ?? false;

    const canShowSwitchButton =
        isSwitchSalesChannelAvailable &&
        (productSwitcherTasks.length === 0 ||
            (productSwitcherTasks.length > 0 &&
                productSwitcherTasks.every(
                    (task) => task.state !== TASKSTATUS.Waiting && task.state !== TASKSTATUS.Processing && task.state !== TASKSTATUS.Error
                )));

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Platform', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    const buildRowCells = (salesChannel: salesChannelsActive$data['salesChannels'][number]) => {
        const name: string = salesChannel.detail?.properties?.name || '';

        const storeUrl = salesChannel.ecommerceSettings?.definitiveUrl || '';

        const storeNumber = storeNumbers.find((x) => x.salesChannelID === salesChannel.id)?.storeNumber || 1;

        // App-based master integrations can only be disconnected by removing the Zakeke app from the store.
        const cannotDisconnect =
            salesChannel.id === 'guid://Zakeke/SalesChannel/1' && ['SHOPIFY', 'BIG_COMMERCE', 'WIX'].includes(salesChannel.ecommerceSettings!.ecommerceName);

        return [
            <StyledDivElementTable>
                <StoreNumberCell>
                    {T._(`Store ${storeNumber}`, 'Admin')}
                    {toLocalID(salesChannel.id) == 1 && <Badge variant='info'>{T._('Master', 'Admin')}</Badge>}
                </StoreNumberCell>
            </StyledDivElementTable>,

            <InlineRenameCell name={name} onRename={(newName) => handleInlineRenameSalesChannel(salesChannel, newName)} />,
            <StyledDivElementTable>
                <StyledLinkContainer>
                    <StyledLink href={storeUrl} target='_blank' title={storeUrl}>
                        <span>{storeUrl}</span>
                    </StyledLink>
                </StyledLinkContainer>
            </StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledImg>{SalesChannelIcon(salesChannel.ecommerceSettings?.ecommerceName, false)}</StyledImg>
            </StyledDivElementTable>,
            <ActionsColContainer>
                {!cannotDisconnect ? (
                    <ZTooltip position={'top'} title={T._('Disconnect', 'Admin')}>
                        <GhostButton
                            $size='small'
                            $type='icon'
                            aria-label={T._('Disconnect', 'Admin')}
                            onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                        >
                            <Icon>
                                <LinkOffIcon />
                            </Icon>
                        </GhostButton>
                    </ZTooltip>
                ) : (
                    <ZTooltip position={'top'} title={T._('To disconnect, remove the app from your store.', 'Admin')}>
                        <UnclickableDisconnectButtonContainer>
                            <GhostButton $size='small' $type='icon' disabled aria-label={T._('Disconnect', 'Admin')}>
                                <Icon>
                                    <LinkOffIcon />
                                </Icon>
                            </GhostButton>
                        </UnclickableDisconnectButtonContainer>
                    </ZTooltip>
                )}
                <DropdownActions
                    header={
                        <Icon $hoverable>
                            <VerticalThreePoint />
                        </Icon>
                    }
                    sections={[
                        [
                            storeUrl && (
                                <DropdownActionItem key={'view_store_button_' + salesChannel.id} onClick={() => window.open(storeUrl, '_blank')}>
                                    {T._('View store', 'Admin')}
                                </DropdownActionItem>
                            ),
                            <DropdownActionItem
                                key={'API_key_button_' + salesChannel.id}
                                onClick={() => {
                                    handleIntegrationSettingsAndApiKey(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                }}
                            >
                                {['API', 'C_CLOUD'].includes(salesChannel.ecommerceSettings!.ecommerceName)
                                    ? T._('Integration settings and API Keys', 'Admin')
                                    : T._('API Keys', 'Admin')}
                            </DropdownActionItem>,
                            ,
                            salesChannel.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP' && (
                                <DropdownActionItem
                                    key={'edit_store_url_button_' + salesChannel.id}
                                    onClick={() => {
                                        handleChangeStoreUrlClick(salesChannel);
                                    }}
                                >
                                    {T._('Change store URL', 'Admin')}
                                </DropdownActionItem>
                            ),
                            salesChannel.ecommerceSettings?.ecommerceName &&
                                !['API', 'C_CLOUD', 'NONE'].includes(salesChannel.ecommerceSettings.ecommerceName) && (
                                    <DropdownActionItem
                                        key={'integration_settings_button_' + salesChannel.id}
                                        onClick={() => {
                                            const platform = salesChannel.ecommerceSettings!.ecommerceName;
                                            const url =
                                                platform === 'SHOPIFY'
                                                    ? backofficeUrl(`/settings/integration-settings/SHOPIFY?salesChannelID=${toLocalID(salesChannel.id)}`)
                                                    : backofficeUrl(`/settings/integration-settings/${platform}`);
                                            navigate(url);
                                        }}
                                    >
                                        {T._('Go to Integration Settings', 'Admin')}
                                    </DropdownActionItem>
                                ),
                            (canShowSwitchButton ||
                                productSwitcherTasks.some(
                                    (task) =>
                                        task.taskType === 'PAYMENT_CONFIRMED' &&
                                        (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing) &&
                                        task.extraData.secondarySalesChannelID?.toString() === toLocalID(salesChannel.id)?.toString()
                                )) &&
                                salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                salesChannel.id !== 'guid://Zakeke/SalesChannel/1' && (
                                    <DropdownActionItem
                                        key={'switch_sales_channel' + salesChannel.id}
                                        disabled={checkingSellerPlanForChannelID !== null}
                                        onClick={() => {
                                            handleSwitchSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                        }}
                                    >
                                        {checkingSellerPlanForChannelID === salesChannel.id ? <Spinner /> : T._('Set as Master Integration', 'Admin')}
                                    </DropdownActionItem>
                                )
                        ],
                        [
                            <DropdownActionItem
                                key={'disconnect_' + salesChannel.id}
                                danger
                                disabled={cannotDisconnect}
                                onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                            >
                                {T._('Disconnect', 'Admin')}
                            </DropdownActionItem>
                        ]
                    ]}
                    orientation={'RIGHT'}
                />
            </ActionsColContainer>
        ];
    };

    const salesChannelIDs = activeSalesChannels.salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE').map((channel) => channel.id || '');

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const masterEcommerceSettings = masterSalesChannel[0]?.ecommerceSettings;

    const canShowAddSalesChannelDialog = searchParams.get('showAddSalesChannelDialog') || 'false';

    const isFirstSalesChannel = !masterEcommerceSettings?.ecommerceGlobalID;

    const showBackButton = !(masterSalesChannel?.length > 0 && otherSalesChannel?.length == 0 && added === 'true');

    useEffect(() => {
        if (added === 'true' && !localStorage.getItem('pendingAction')) {
            showSuccessNotification({
                message: isMaster ? T._('Your Integration has been saved.', 'Admin') : T._('The updated list of sales channels will be shown soon', 'Admin'),
                dismiss: { duration: 5000 }
            });

            const timerId = setTimeout(() => {
                startTransition(() => setFetchKey((prev) => prev + 1));
                navigate(backofficeUrl('/sales-channels'), { replace: true });
            }, 5000);

            return () => clearTimeout(timerId);
        }
    }, [added, isMaster, location.key]);

    useEffect(() => {
        if (canShowAddSalesChannelDialog === 'true') {
            handleAddSaleChannel();
        }
    }, [canShowAddSalesChannelDialog]);

    useEffect(() => {
        // trigger every time the list of channels changes
        const pending = JSON.parse(localStorage.getItem('pendingAction') ?? 'null');
        if (!pending || pending.type !== 'publish-product') return;

        const integrationDone = activeSalesChannels.salesChannels.some((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');
        if (!integrationDone) return;

        if (Date.now() - pending.ts > 10 * 60_000) {
            localStorage.removeItem('pendingAction');
            return;
        }

        localStorage.removeItem('pendingAction');

        navigate(backofficeUrl(pending.redirectPage), {
            replace: true,
            state: { doPublishImmediately: true, productID: pending.productId }
        });
    }, [activeSalesChannels.salesChannels]);

    // Handle payment success return from Shopify
    useEffect(() => {
        if (paymentSuccess === 'true' && salesChannelIDParam && chargeId) {
            // First, confirm the Shopify charge
            confirmShopifyCharge({
                variables: {
                    input: {
                        shopifyChargeID: chargeId,
                        chargeType: 'SWITCH_SALES_CHANNELS'
                    }
                },
                onCompleted: () => {
                    // Clear query params after successful confirmation
                    setSearchParams({});

                    // Persist payment state to DB and show dialog
                    persistPaymentAndShowDialog(salesChannelIDParam, masterSalesChannelIDParam || '', true, chargeId, true); // checkbox must have been checked to initiate payment
                },
                onError: () => {
                    // Clear query params even on error
                    setSearchParams({});

                    showErrorNotification({
                        message: T._('Failed to confirm payment. Please contact support.', 'Admin')
                    });
                }
            });
        }
    }, [paymentSuccess, salesChannelIDParam, chargeId]);

    // Handle return from Shopify after the seller re-approved the SAME plan (step 3 of the redefined Shopify switch flow).
    // The backend (/Subscription/ActivateShopify) already activated the recurring charge on the new master store and, because
    // handleRevertAndRedirectToShopify passed a returnUrl, redirected back here with switchSubscriptionApproved=true. We read the
    // sales-channel IDs persisted before the redirect and continue with the DeleteShopifyRecurringChargesTask, then the switcher.
    useEffect(() => {
        if (switchSubscriptionApproved !== 'true') return;

        const stored = localStorage.getItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY);
        localStorage.removeItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY);
        setSearchParams({});

        if (!stored) return;
        const { masterSalesChannelID, secondarySalesChannelID } = JSON.parse(stored) as {
            masterSalesChannelID: string;
            secondarySalesChannelID: string;
        };
        if (masterSalesChannelID && secondarySalesChannelID) {
            handleDeleteRecurringChargesAndSwitch(secondarySalesChannelID, masterSalesChannelID);
        }
    }, [switchSubscriptionApproved]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restore payment success dialog from DB on page load
    useEffect(() => {
        // Skip if URL params will handle it (payment just completed)
        if (paymentSuccess === 'true') return;
        // Skip while the seller is mid-switch beyond the payment step: returning from the Shopify plan approval
        // (switchSubscriptionApproved, handled by the switch-return effect) or a switch sub-flow task is already polling.
        if (switchSubscriptionApproved === 'true') return;
        if (chargeId) return;
        if (localStorage.getItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY)) return;
        if (localStorage.getItem(UNSUBSCRIBE_TASK_STORAGE_KEY)) return;

        const pendingPaymentTask = productSwitcherTasks.find(
            (task) => task.taskType === 'PAYMENT_CONFIRMED' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
        );

        if (!pendingPaymentTask) return;

        const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        // Convert local IDs to global IDs for comparison
        const masterGlobalID = toGlobalID(masterSalesChannelID, 'SalesChannel');
        const secondaryGlobalID = toGlobalID(secondarySalesChannelID, 'SalesChannel');

        // Determine if Shopify by checking ecommerce name of the sales channels
        const isShopify = activeSalesChannels.salesChannels.some(
            (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
    }, [productSwitcherTasks]);

    // Handle return from subscription page (redirected from dashboard)
    useEffect(() => {
        const subscriptionComplete = searchParams.get('subscriptionComplete');

        if (subscriptionComplete === 'true') {
            // Find the last completed EDITSUBSCRIPTION task to get the sales channel IDs
            const completedEditSubscriptionTasks = productSwitcherTasks.filter(
                (task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed
            );

            // Get the task with the highest taskID (most recent)
            const lastEditSubscriptionTask =
                completedEditSubscriptionTasks.length > 0
                    ? completedEditSubscriptionTasks.reduce((max, task) => (task.taskID > max.taskID ? task : max))
                    : null;

            const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
            const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

            // Only proceed if we have both IDs
            if (masterSalesChannelID && secondarySalesChannelID) {
                // Do not show the dialog if the switch task is already running
                const hasPendingSwitchTask = productSwitcherTasks.some(
                    (task) => task.taskType === 'SWITCHSALESCHANNELS' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
                );
                if (hasPendingSwitchTask) return;

                // Clear query params
                setSearchParams({});

                // Show final dialog to complete the switch
                showDialog(
                    'complete-sales-channel-switch',
                    <MessageDialog
                        showCloseButton={false}
                        message={
                            <>
                                <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                                <p>{T._('Thank you for subscribing to your new plan!', 'Admin')}</p>
                                <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                                <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                                <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                                    {T._('This process will run in the background and you will be notified when it completes.', 'Admin')}
                                </p>
                            </>
                        }
                        buttons={[
                            {
                                label: T._('Switch Products', 'Admin'),
                                onClick: () => {
                                    closeDialog('complete-sales-channel-switch');
                                    handleStartProductSwitch(
                                        toGlobalID(masterSalesChannelID, 'SalesChannel'),
                                        toGlobalID(secondarySalesChannelID, 'SalesChannel')
                                    );
                                }
                            }
                        ]}
                    />
                );
            } else {
                // If we can't find the task, clear params and show error
                setSearchParams({});
                showErrorNotification({
                    message: T._('Unable to complete sales channel switch. Please try again or contact support.', 'Admin')
                });
            }
        }
    }, [searchParams, setSearchParams, showDialog, closeDialog, productSwitcherTasks]);

    // Show "Complete Sales Channel Switch" dialog on page refresh when migration is pending
    useEffect(() => {
        if (!isSellerMigrating) return;
        if (searchParams.get('subscriptionComplete') === 'true') return;
        if (
            productSwitcherTasks.some(
                (task) => task.taskType === 'SWITCHSALESCHANNELS' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
            )
        )
            return;

        const lastEditSubscriptionTask = productSwitcherTasks
            .filter((task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed)
            .reduce<(typeof productSwitcherTasks)[number] | null>((max, task) => (max === null || task.taskID > max.taskID ? task : max), null);

        const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
        const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        showDialog(
            'complete-sales-channel-switch',
            <MessageDialog
                showCloseButton={false}
                message={
                    <>
                        <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                        <p>{T._('Thank you for subscribing to your new plan!', 'Admin')}</p>
                        <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                        <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                            {T._('This process will run in the background and you will be notified when it completes.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[
                    {
                        label: T._('Switch Products', 'Admin'),
                        onClick: () => {
                            closeDialog('complete-sales-channel-switch');
                            handleStartProductSwitch(
                                toGlobalID(masterSalesChannelID.toString(), 'SalesChannel'),
                                toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel')
                            );
                        }
                    }
                ]}
            />
        );
    }, [isSellerMigrating, searchParams, productSwitcherTasks, showDialog, closeDialog]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    // Restore unsubscribing-plan dialog after a page refresh
    useEffect(() => {
        const stored = localStorage.getItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
        if (!stored) return;

        const { taskID, salesChannelID, masterSalesChannelID, flowType, dialogId } = JSON.parse(stored) as {
            taskID: string;
            salesChannelID: string;
            masterSalesChannelID: string;
            flowType?: SwitchPollingFlowType;
            dialogId?: string;
        };

        // The resume message and completion callback depend on which stage of the switch was in flight when the page reloaded.
        let resumeMessage = T._('Processing your subscription cancellation...', 'Admin');
        let resumeCallback: (() => void) | undefined;
        if (flowType === 'nonShopify') {
            resumeMessage = T._('Generating sales channel switch task...', 'Admin');
            resumeCallback = () => handleStartProductSwitch(masterSalesChannelID, salesChannelID);
            // TASK_TYPE_SWITCH_INTEGRATION_CONTEXT is no longer used — the switchIntegrationContext resume stage is disabled.
            // } else if (flowType === 'switchIntegrationContext') {
            //     resumeMessage = T._('Preparing the sales channel switch...', 'Admin');
            //     resumeCallback = () => handleRevertAndRedirectToShopify(salesChannelID, masterSalesChannelID);
        } else if (flowType === 'deleteRecurringCharges') {
            resumeMessage = T._('The sales channel switch task is being created...', 'Admin');
            resumeCallback = () => handleStartProductSwitch(masterSalesChannelID, salesChannelID);
        }

        showDialog(
            dialogId ?? 'unsubscribing-plan',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{resumeMessage}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        startPollingTask(taskID, salesChannelID, masterSalesChannelID, resumeCallback, flowType, dialogId ?? 'unsubscribing-plan');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEditSubscriptionForNonShopify = (salesChannelID: string, masterSalesChannelID: string) => {
        showDialog(
            'unsubscribing-plan',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Generating sales channel switch task...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('The sales channel switch task is being generated. Products will be migrated once complete.', 'Admin')}
                        </p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        generateUnsubscribeTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: salesChannelID,
                    environment: 'CUSTOMIZER'
                }
            },
            onCompleted: (response) => {
                if (!response.generateEditSubscriptionTask) {
                    closeDialog('unsubscribing-plan');
                    showErrorNotification({
                        message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                    });
                    return;
                }
                const taskID = response.generateEditSubscriptionTask.taskID;
                startPollingTask(
                    taskID.toString(),
                    salesChannelID,
                    masterSalesChannelID,
                    () => handleStartProductSwitch(masterSalesChannelID, salesChannelID),
                    'nonShopify'
                );
            },
            onError: () => {
                closeDialog('unsubscribing-plan');
                showErrorNotification({
                    message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                });
            }
        });
    };

    const startPollingTask = (
        taskID: string,
        salesChannelID: string,
        masterSalesChannelID: string,
        onCompleted?: () => void,
        flowType?: SwitchPollingFlowType,
        dialogId: string = 'unsubscribing-plan'
    ) => {
        // Convert taskID to global ID format for the query
        const globalTaskID = toGlobalID(taskID, 'ProductSwitcherTask');

        // Persist task info so the dialog can be restored after a page refresh
        localStorage.setItem(UNSUBSCRIBE_TASK_STORAGE_KEY, JSON.stringify({ taskID, salesChannelID, masterSalesChannelID, flowType, dialogId }));

        // Poll every 3 seconds
        pollingIntervalRef.current = setInterval(() => {
            fetchQuery<salesChannelPollUnsubscribeTaskQuery>(Environment, pollUnsubscribeTaskQuery, { id: globalTaskID })
                .toPromise()
                .then((data) => {
                    if (!data?.productSwitcherTask) return;

                    const task = data.productSwitcherTask;

                    // Check if task is completed (state === 2)
                    if (task.state === TASKSTATUS.Completed) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog(dialogId);
                        if (onCompleted) {
                            onCompleted();
                        } else {
                            window.location.reload();
                        }
                    } else if (task.state === TASKSTATUS.Error) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog(dialogId);

                        // Show error dialog suggesting to contact support
                        showDialog(
                            'unsubscribe-task-error',
                            <MessageDialog
                                showCloseButton={true}
                                message={
                                    <>
                                        <h3 style={{ color: '#d32f2f', marginBottom: '16px' }}>{T._('Sales Channel Switch Failed', 'Admin')}</h3>
                                        <p>{T._('An error occurred while processing the sales channel switch.', 'Admin')}</p>
                                        {task.lastError && (
                                            <p style={{ marginTop: '8px', fontSize: '13px', color: '#d32f2f' }}>
                                                {T._('Error details:', 'Admin')} {task.lastError}
                                            </p>
                                        )}
                                        <p style={{ marginTop: '16px' }}>{T._('Please contact the Zakeke support team for assistance:', 'Admin')}</p>
                                        <p style={{ marginTop: '8px' }}>
                                            {T._('Email:', 'Admin')} <a href='mailto:support@zakeke.com'>support@zakeke.com</a>
                                        </p>
                                    </>
                                }
                                buttons={[
                                    {
                                        label: T._('Close', 'Admin'),
                                        onClick: () => closeDialog('unsubscribe-task-error')
                                    }
                                ]}
                            />
                        );
                    }
                    // Otherwise continue polling (states 0=Waiting or 1=Processing)
                })
                .catch(() => {
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                    }
                    if (pollingTimeoutRef.current) {
                        clearTimeout(pollingTimeoutRef.current);
                    }
                    localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                    closeDialog(dialogId);

                    showErrorNotification({
                        message: T._('Failed to check task status. Please refresh the page.', 'Admin')
                    });
                });
        }, 5000); // Poll every 5 seconds

        // Stop polling after 2 hours and recommend contacting support
        pollingTimeoutRef.current = setTimeout(() => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
            closeDialog(dialogId);
            showDialog(
                'unsubscribe-task-timeout',
                <MessageDialog
                    showCloseButton={true}
                    message={
                        <>
                            <h3 style={{ marginBottom: '16px' }}>{T._('Sales Channel Switch Taking Too Long', 'Admin')}</h3>
                            <p>{T._('The sales channel switch has been running for more than 2 hours without completing.', 'Admin')}</p>
                            <p style={{ marginTop: '16px' }}>{T._('Please contact the Zakeke support team for assistance:', 'Admin')}</p>
                            <p style={{ marginTop: '8px' }}>
                                {T._('Email:', 'Admin')} <a href='mailto:support@zakeke.com'>support@zakeke.com</a>
                            </p>
                        </>
                    }
                    buttons={[
                        {
                            label: T._('Close', 'Admin'),
                            onClick: () => closeDialog('unsubscribe-task-timeout')
                        }
                    ]}
                />
            );
        }, 7200000); // 2 hours timeout
    };

    const handleStartProductSwitch = (masterSalesChannelID: string, secondarySalesChannelID: string) => {
        // Use generateProductSwitcherTask mutation
        generateProductSwitcherTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateProductSwitcherTask?.taskID;
                // Full page reload to task monitoring page to clear any open dialogs
                window.location.href = backofficeUrl('/sales-channels/switch-tasks') + (taskID != null ? `?newTaskId=${taskID}` : '');
            },
            onError: () => {
                showErrorNotification({
                    message: T._('Failed to start product switch. Please try again.', 'Admin')
                });
            }
        });
    };

    // TASK_TYPE_SWITCH_INTEGRATION_CONTEXT is no longer used — the switch-integration-context step is skipped in the Shopify flow.
    // handleSwitchIntegrationContext is commented out; the Shopify flow now goes straight to handleRevertAndRedirectToShopify.
    /*
    const handleSwitchIntegrationContext = (secondarySalesChannelID: string, masterSalesChannelID: string) => {
        showDialog(
            'switch-integration-context',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Preparing the sales channel switch...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        generateSwitchIntegrationContextTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateSwitchIntegrationContextTask?.taskID;
                if (!taskID) {
                    closeDialog('switch-integration-context');
                    showErrorNotification({
                        message: T._('Failed to start the sales channel switch. Please try again.', 'Admin')
                    });
                    return;
                }
                startPollingTask(
                    taskID.toString(),
                    secondarySalesChannelID,
                    masterSalesChannelID,
                    () => handleRevertAndRedirectToShopify(secondarySalesChannelID, masterSalesChannelID),
                    'switchIntegrationContext',
                    'switch-integration-context'
                );
            },
            onError: () => {
                closeDialog('switch-integration-context');
                showErrorNotification({
                    message: T._('Failed to start the sales channel switch. Please try again.', 'Admin')
                });
            }
        });
    };
    */

    // Shopify switch flow (after payment): redirect the seller to Shopify to approve the recurring charge for the SAME plan
    // they already have, then on return delete the old recurring charges and run the product switcher. TASK_TYPE_REVERT_INTEGRATION_CONTEXT
    // is no longer used, so no revert task is fired before the redirect. The plan ID and sales-channel IDs are persisted so the
    // return handler (and a page refresh) can resume the flow.
    const handleRevertAndRedirectToShopify = (secondarySalesChannelID: string, masterSalesChannelID: string) => {
        const planId = query.subscription?.planId;
        if (planId == null) {
            showErrorNotification({
                message: T._('Unable to determine your current plan. Please contact support.', 'Admin')
            });
            return;
        }

        showDialog(
            'redirecting-to-shopify',
            <MessageDialog
                showCloseButton={false}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Redirecting you to Shopify to approve your plan...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>{T._('Please do not close this window.', 'Admin')}</p>
                    </>
                }
                buttons={[]}
            />
        );

        localStorage.setItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY, JSON.stringify({ masterSalesChannelID, secondarySalesChannelID }));

        // TASK_TYPE_REVERT_INTEGRATION_CONTEXT is no longer used — the revert-integration-context task is skipped.
        // The redirect below (previously the task's onCompleted) now runs directly after persisting the return state.
        /*
        generateRevertIntegrationContextTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: () => {
                const returnUrl = `${window.location.origin}${backofficeUrl('/sales-channels')}?switchSubscriptionApproved=true`;
                window.location.href = backofficeUrl(`/seller/redirectForUpgradePlanForShopify?planID=${planId}&returnUrl=${encodeURIComponent(returnUrl)}`);
            },
            onError: () => {
                localStorage.removeItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY);
                closeDialog('redirecting-to-shopify');
                showErrorNotification({
                    message: T._('Failed to prepare the plan approval. Please try again.', 'Admin')
                });
            }
        });
        */

        const salesChannelID = toLocalID(secondarySalesChannelID) || -1;

        if (salesChannelID === -1) {
            showErrorNotification({
                message: T._('There is an error with the sales channel code. Please contact us at help@zakeke.com', 'Admin')
            });
            return;
        }

        // Reuse the "change plan" redirect (auto-POSTs planID to /Subscription/SubscribeShopify) so the seller approves
        // the same plan on Shopify without the plan-selection page. We pass a same-origin returnUrl so the backend
        // redirects back to /sales-channels (instead of /Dashboard?planChanged=true) once the charge is activated.
        // salesChannelID is the secondary (new master-to-be) sales channel — the endpoint activates the new recurring charge on it.
        const returnUrl = `${window.location.origin}${backofficeUrl('/sales-channels')}?switchSubscriptionApproved=true`;
        window.location.href = backofficeUrl(
            `/seller/redirectForUpgradePlanForShopify?planID=${planId}&salesChannelID=${encodeURIComponent(salesChannelID)}&returnUrl=${encodeURIComponent(returnUrl)}`
        );
    };

    // Step 4: after the seller re-approves the plan on Shopify and the charge is verified, delete the Shopify recurring charges
    // of the secondary sales channel (the one being promoted to master), then run the product switcher and redirect to the switch-tasks page.
    const handleDeleteRecurringChargesAndSwitch = (secondarySalesChannelID: string, masterSalesChannelID: string) => {
        showDialog(
            'creating-switch-task',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('The sales channel switch task is being created...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        generateDeleteShopifyRecurringChargesTask({
            variables: {
                input: {
                    salesChannelID: masterSalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateDeleteShopifyRecurringChargesTask?.taskID;
                if (!taskID) {
                    closeDialog('creating-switch-task');
                    showErrorNotification({
                        message: T._('Failed to create the sales channel switch task. Please try again.', 'Admin')
                    });
                    return;
                }
                startPollingTask(
                    taskID.toString(),
                    secondarySalesChannelID,
                    masterSalesChannelID,
                    () => handleStartProductSwitch(masterSalesChannelID, secondarySalesChannelID),
                    'deleteRecurringCharges',
                    'creating-switch-task'
                );
            },
            onError: () => {
                closeDialog('creating-switch-task');
                showErrorNotification({
                    message: T._('Failed to create the sales channel switch task. Please try again.', 'Admin')
                });
            }
        });
    };

    const showPaymentSuccessDialogForSwitch = (salesChannelID: string, masterSalesChannelID: string, isShopify: boolean) => {
        showDialog(
            'payment-success-dialog',
            <PaymentSuccessDialog
                salesChannelID={salesChannelID}
                isShopify={isShopify}
                onProceedToNextStep={() => {
                    closeDialog('payment-success-dialog');
                    if (isShopify) {
                        // Shopify: re-approve the SAME plan on Shopify → delete old recurring charges → product switch.
                        // TASK_TYPE_SWITCH_INTEGRATION_CONTEXT is no longer used, so we skip straight to the plan re-approval redirect.
                        handleRevertAndRedirectToShopify(toGlobalID(salesChannelID, 'SalesChannel'), toGlobalID(masterSalesChannelID, 'SalesChannel'));
                    } else {
                        // Non-Shopify: generate EditSubscriptionTask first, then ProductSwitcherTask on completion
                        handleEditSubscriptionForNonShopify(toGlobalID(salesChannelID, 'SalesChannel'), toGlobalID(masterSalesChannelID, 'SalesChannel'));
                    }
                }}
                onCancel={() => {
                    closeDialog('payment-success-dialog');
                }}
            />
        );
    };

    const persistPaymentAndShowDialog = (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => {
        createPaymentConfirmedTask({
            variables: {
                input: {
                    masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                    secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel'),
                    shopifyChargeID: shopifyChargeID,
                    hasAcknowledgedInformation: hasAcknowledgedInfo
                }
            },
            onCompleted: () => {
                refetch({}, { fetchPolicy: 'network-only' });
            }
        });

        showPaymentSuccessDialogForSwitch(salesChannelID, masterSalesChannelID, isShopify);
    };

    const handleAddSaleChannel = () => {
        if (isFirstSalesChannel) {
            navigate(backofficeUrl('/connect-shop'));
            return;
        }

        query.shopifyAuthEcommerceSettings?.isTestStore && config.isProduction
            ? showDialog(
                  'add-sales-channel-not-available',
                  <MessageDialog
                      windowDecorator={SalesChannelNotAvailableDialogWindow}
                      message={
                          <>
                              {T._(
                                  "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                  'Admin'
                              )}
                              <br />
                              <br />
                              {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                              <br />
                              <br />
                              {T._('Thank you for your understanding.', 'Admin')}
                          </>
                      }
                      buttons={[
                          {
                              label: T._('OK', 'Admin'),
                              onClick: () => {
                                  closeDialog('add-sales-channel-not-available');
                              }
                          }
                      ]}
                  />
              )
            : showDialog(
                  'add-sales-channel',
                  <AddSalesChannelDialog
                      onSalesChannelCreated={() => {
                          closeDialog('add-sales-channel');
                          setFetchKey((prev) => prev + 1);
                      }}
                  ></AddSalesChannelDialog>
              );
    };

    const handleCreateMiniSite = () => {
        const eligibleChannels = activeSalesChannels.salesChannels.filter(
            (sc) => sc.ecommerceSettings?.ecommerceName === 'NONE' || sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showDialog(
            'create-standalone-site',
            <CreateStandaloneSiteDialog
                salesChannels={eligibleChannels}
                onCreated={(site) => {
                    closeDialog('create-standalone-site');
                    navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(site.standaloneSiteID.toString())}/settings`));
                }}
            />
        );
    };

    const handleDeactivateSalesChannel = (id: string, ecommerce: ECommerce) => {
        const channel = activeSalesChannels.salesChannels.find((c) => c.id === id);
        const storeLabel = channel?.detail?.properties?.name || channel?.ecommerceSettings?.definitiveUrl || '';

        if (ecommerce === 'SHOPIFY' || ecommerce === 'BIG_COMMERCE') {
            showDialog(
                'disconnect-channel',
                <MessageDialog
                    message={T._('You must remove the Zakeke app from your store to disconnect this sales channel', 'Admin')}
                    onClose={() => {
                        closeDialog('disconnect-channel');
                    }}
                />
            );
        } else if (toLocalID(id) == 1) {
            showDialog(
                'disconnect-channel',
                <QuestionDialog
                    showCloseButton={false}
                    title={T._('Disconnect sales channel', 'Admin')}
                    message={
                        <>
                            {T._(
                                'Some of the products in your Customizable Products list originate from this sales channel. If you disconnect it, they will no longer be available for customization by your customers even on other sales channels.',
                                'Admin'
                            )}
                            {storeLabel && (
                                <>
                                    <br />
                                    <br />
                                    {T._('Sales channel:', 'Admin')} <strong>{storeLabel}</strong>
                                </>
                            )}
                            <br />
                            <br />
                            <Suspense fallback={<InfoText>{T._('Loading affected products...', 'Admin')}</InfoText>}>
                                <DefaultSalesChannelProductsList />
                            </Suspense>
                        </>
                    }
                    buttonYesLabel={T._('Cancel', 'Admin')}
                    buttonNoLabel={T._('Disconnect Anyway', 'Admin')}
                    destructiveNo
                    onYesClick={() => closeDialog('disconnect-channel')}
                    onNoClick={() => {
                        closeDialog('disconnect-channel');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        removeMasterIntegration({
                            variables: {
                                salesChannelID: null // Master integration
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                setFetchKey((prev) => prev + 1);
                                if (added === 'true') {
                                    navigate(backofficeUrl('/sales-channels'));
                                }
                            }
                        });
                    }}
                />
            );
        } else {
            showDialog(
                'disconnect-channel',
                <QuestionDialog
                    showCloseButton={false}
                    title={T._('Disconnect sales channel', 'Admin')}
                    message={T._('Do you want to disconnect the sales channel?', 'Admin')}
                    buttonYesLabel={T._('Cancel', 'Admin')}
                    buttonNoLabel={T._('Disconnect', 'Admin')}
                    destructiveNo
                    onYesClick={() => closeDialog('disconnect-channel')}
                    onNoClick={() => {
                        closeDialog('disconnect-channel');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        setSalesChannelStatus({
                            variables: {
                                id: id,
                                status: 'DISABLED'
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                refetch({}, { fetchPolicy: 'network-only' });
                            }
                        });
                    }}
                />
            );
        }
    };

    const handleInlineRenameSalesChannel = (salesChannel: salesChannelsActive$data['salesChannels'][number], newName: string) => {
        inlineRenameSalesChannel({
            variables: {
                input: {
                    id: salesChannel.detail?.id,
                    salesChannelGlobalID: salesChannel.id,
                    properties: { name: newName }
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed rename sales channel', 'Admin') });
            },
            onCompleted: () => {
                refetch({}, { fetchPolicy: 'network-only' });
            }
        });
    };

    const handleIntegrationSettingsAndApiKey = (id: string, ecommerce: string) => {
        if (ecommerce === 'API') {
            showDialog('api-connection', <ApiIntegrationSalesChannelDialog salesChannelID={id || ''} hasMasterIntegration={true} />);
        } else if (ecommerce === 'C_CLOUD') {
            showDialog('sales-force', <CCloudIntegrationSalesChannelDialog salesChannelID={id || ''} />);
        } else {
            showDialog('api-key', <ApiKeyDialog salesChannelID={id} />);
        }
    };

    const handleChangeStoreUrlClick = (salesChannel: salesChannelsActive$data['salesChannels'][0]) => {
        showDialog('store-url-sales-channel', <StoreUrlSalesChannelDialog salesChannel={salesChannel} />);
    };

    const openSwitchSalesChannelDialog = (
        salesChannelID: string,
        secondaryEcommerceName: string,
        planCheck?: SellerPlanCheckInfo,
        configuratorModelsCount?: number,
        hasPodProducts?: boolean
    ) => {
        const secondaryEcommerceSettings = activeSalesChannels.salesChannels.find((sc) => sc.id === salesChannelID)?.ecommerceSettings;

        showDialog(
            'switch-sales-channel-dialog',
            <SwitchSalesChannelDialog
                secondarySalesChannelID={salesChannelID}
                secondaryEcommerceName={secondaryEcommerceName}
                secondaryCurrencyCode={secondaryEcommerceSettings?.currencyCode}
                masterSalesChannelID={masterEcommerceSettings?.id || ''}
                masterEcommerceName={masterEcommerceSettings?.ecommerceName || 'NONE'}
                masterCurrencyCode={masterEcommerceSettings?.currencyCode}
                planCheck={planCheck}
                configuratorModelsCount={configuratorModelsCount}
                hasPodProducts={hasPodProducts}
                onClosed={() => {
                    closeDialog('switch-sales-channel-dialog');
                    refetch({}, { fetchPolicy: 'network-only' });
                }}
                onPaymentSuccess={(scID, masterScID, isShopify, chargeID, hasAcknowledgedInfo) => {
                    persistPaymentAndShowDialog(scID, masterScID, isShopify, chargeID, hasAcknowledgedInfo);
                }}
            />
        );
    };

    const handleSwitchSalesChannel = (salesChannelID: string, secondaryEcommerceName: string) => {
        // If the secondary store is not Shopify/API and the master integration is API, the switch
        // requires manual intervention — prompt the user to contact support.
        if (secondaryEcommerceName !== 'SHOPIFY' && secondaryEcommerceName !== 'API' && masterEcommerceSettings?.ecommerceName === 'API') {
            showDialog(
                'switch-not-supported',
                <MessageDialog
                    message={
                        <>
                            <h3>{T._('Switch not supported', 'Admin')}</h3>
                            <p>
                                {T._(
                                    'Switching this store to Master Integration is not supported when the current Master Integration is API. Please contact Zakeke support for assistance.',
                                    'Admin'
                                )}
                            </p>
                        </>
                    }
                    buttons={[{ label: T._('OK', 'Admin'), onClick: () => closeDialog('switch-not-supported') }]}
                />
            );
            return;
        }

        // If a PAYMENT_CONFIRMED task is already waiting for this channel, the user paid
        // but closed the success dialog — reopen it instead of restarting the payment flow.
        const localID = toLocalID(salesChannelID)?.toString();
        const pendingPaymentTask = productSwitcherTasks.find(
            (task) =>
                task.taskType === 'PAYMENT_CONFIRMED' &&
                (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing) &&
                task.extraData.secondarySalesChannelID?.toString() === localID
        );

        if (pendingPaymentTask) {
            const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
            if (masterSalesChannelID && secondarySalesChannelID) {
                const masterGlobalID = toGlobalID(masterSalesChannelID.toString(), 'SalesChannel');
                const secondaryGlobalID = toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel');
                const isShopify = activeSalesChannels.salesChannels.some(
                    (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
                );
                showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
                return;
            }
        }

        const proceedWithSellerPlanCheck = () => {
            fetchQuery<salesChannelCheckSellerPlanQuery>(Environment, checkSellerPlanQuery, {})
                .toPromise()
                .then((data) => {
                    setCheckingSellerPlanForChannelID(null);
                    openSwitchSalesChannelDialog(
                        salesChannelID,
                        secondaryEcommerceName,
                        data?.checkSellerPlan ?? undefined,
                        data?.modelsCount ?? undefined,
                        data?.hasPodProducts ?? undefined
                    );
                })
                .catch(() => {
                    setCheckingSellerPlanForChannelID(null);
                });
        };

        setCheckingSellerPlanForChannelID(salesChannelID);

        // Shopify development/trial stores can't carry the real recurring charge that becoming
        // Master Integration requires — block the switch instead of letting it fail downstream.
        if (secondaryEcommerceName === 'SHOPIFY' && config.isProduction) {
            fetchQuery<salesChannelCheckShopifyTestStoreQuery>(Environment, checkShopifyTestStoreQuery, {
                salesChannelID: toLocalID(salesChannelID)
            })
                .toPromise()
                .then((data) => {
                    if (data?.shopifyAuthEcommerceSettings?.isTestStore === true) {
                        setCheckingSellerPlanForChannelID(null);
                        showDialog(
                            'switch-test-store-not-allowed',
                            <MessageDialog
                                message={
                                    <>
                                        <h3>{T._('Switch not available', 'Admin')}</h3>
                                        <p>
                                            {T._(
                                                'This store is a Shopify development or trial store, so it cannot be set as the Master Integration. Please use an active Shopify store instead.',
                                                'Admin'
                                            )}
                                        </p>
                                    </>
                                }
                                buttons={[{ label: T._('OK', 'Admin'), onClick: () => closeDialog('switch-test-store-not-allowed') }]}
                            />
                        );
                        return;
                    }

                    proceedWithSellerPlanCheck();
                })
                .catch(() => {
                    setCheckingSellerPlanForChannelID(null);
                });
            return;
        }

        proceedWithSellerPlanCheck();
    };

    const handleViewSwitchTasks = () => {
        navigate(backofficeUrl('/sales-channels/switch-tasks'));
    };

    const hasMasterEcommerce = !!(masterEcommerceSettings?.ecommerceName && masterEcommerceSettings.ecommerceName !== 'NONE');

    const miniSitesCount = isStandaloneEnabled ? (query.standaloneSites?.length ?? 0) : 0;

    // Show the section layout (with empty states) as soon as there is anything to manage.
    // Keep the two-card onboarding hero only when neither an online store nor a mini-site exists.
    const showSections = hasMasterEcommerce || miniSitesCount > 0;

    const pageHeaderDescription = isStandaloneEnabled
        ? T._('Connect your online stores and launch standalone mini-sites, then manage all your sales channels from a single place.', 'Admin')
        : T._('Connect your online stores and manage all your sales channels from a single place.', 'Admin');

    const heroDescription = isStandaloneEnabled
        ? T._(
              'Connect Zakeke to your online store or marketplace, or launch a standalone mini-site, and offer product customization wherever you sell.',
              'Admin'
          )
        : T._('Connect Zakeke to your online store or marketplace and offer product customization wherever you sell.', 'Admin');

    const allStores = [...masterSalesChannel, ...otherSalesChannel];

    return (
        <InlineHelpLayout helpUrl={HELP_URL}>
            {(openHelp) => (
                <StyledContainerBase showBackButton={showBackButton}>
                    {showSections && (
                        <PageHeader
                            title={T._('Sales Channels', 'Admin')}
                            pageKey='sales-channels'
                            backLabel={T._('Back', 'Admin')}
                            description={<>{pageHeaderDescription}</>}
                            learnMoreLabel={T._('Learn more', 'Admin')}
                            learnMoreUrl={HELP_URL}
                            onLearnMoreClick={openHelp}
                            isBackVisible={showBackButton}
                        />
                    )}

                    {showSections ? (
                        <>
                            {added !== 'false' && isPending && Loader}
                            {!isPending && (
                                <Stack>
                                    {hasMasterEcommerce ? (
                                        <CompactSection
                                            title={T._('Online Stores', 'Admin')}
                                            description={T._(
                                                'Online stores are e-commerce platforms connected to Zakeke. The store marked as Master controls billing and product sync for all channels.',
                                                'Admin'
                                            )}
                                            headerActions={
                                                <SectionHeaderActionsGroup>
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && (
                                                        <ZTooltip position='bottom' title={T._('Integration Settings', 'Admin')}>
                                                            <GhostButton
                                                                $size='small'
                                                                $type='icon'
                                                                aria-label={T._('Integration Settings', 'Admin')}
                                                                onClick={() => navigate(backofficeUrl('/settings/integration-settings'))}
                                                            >
                                                                <Icon>
                                                                    <CogIcon />
                                                                </Icon>
                                                            </GhostButton>
                                                        </ZTooltip>
                                                    )}
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && <HeaderActionsDivider />}
                                                    {productSwitcherTasks.length > 0 && (
                                                        <BasicButton onClick={() => handleViewSwitchTasks()}>
                                                            {T._('Sales Channels Tasks', 'Admin')}
                                                        </BasicButton>
                                                    )}
                                                    <PrimaryButton
                                                        $size='medium'
                                                        disabled={state.lastUsedTool === 2}
                                                        onClick={() => handleAddSaleChannel()}
                                                        style={{ minWidth: '160px' }}
                                                    >
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Add Online Store', 'Admin')}
                                                    </PrimaryButton>
                                                </SectionHeaderActionsGroup>
                                            }
                                        >
                                            {state.lastUsedTool === 2 && otherSalesChannel.length > 0 ? (
                                                <ZTooltip
                                                    position={'top'}
                                                    title={T._('Additional Sales Channels are not supported for 3D Configurator', 'Admin')}
                                                >
                                                    <DisabledSalesChannelContainer>
                                                        <Table headings={headings} compact>
                                                            {allStores.map((salesChannel, i) => (
                                                                <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                            ))}
                                                        </Table>
                                                    </DisabledSalesChannelContainer>
                                                </ZTooltip>
                                            ) : (
                                                <Table headings={headings} compact>
                                                    {allStores.map((salesChannel, i) => (
                                                        <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                    ))}
                                                </Table>
                                            )}
                                        </CompactSection>
                                    ) : (
                                        <CompactSection title={T._('Online Stores', 'Admin')}>
                                            <CompactEmptyState>
                                                <EmptyList
                                                    hideIcon
                                                    title={''}
                                                    subTitle={T._(
                                                        'Connect an online store or marketplace and embed Zakeke in your storefront as part of the buying experience.',
                                                        'Admin'
                                                    )}
                                                    buttonTitle={T._('Add Online Store', 'Admin')}
                                                    disabledButton={state.lastUsedTool === 2}
                                                    onButtonClick={() => handleAddSaleChannel()}
                                                />
                                            </CompactEmptyState>
                                        </CompactSection>
                                    )}

                                    {isStandaloneEnabled && (
                                        <Suspense
                                            fallback={
                                                <CompactSection title={T._('Mini-Sites', 'Admin')}>
                                                    <Center>
                                                        <Spinner />
                                                    </Center>
                                                </CompactSection>
                                            }
                                        >
                                            <MiniSitesList
                                                fetchKey={miniSitesFetchKey}
                                                onChanged={() => {
                                                    setMiniSitesFetchKey((prev) => prev + 1);
                                                    setFetchKey((prev) => prev + 1);
                                                }}
                                                onAdd={() => handleCreateMiniSite()}
                                            />
                                        </Suspense>
                                    )}
                                </Stack>
                            )}
                        </>
                    ) : (
                        <>
                            {added !== 'false' && Loader}
                            {added === 'false' && (
                                <>
                                    <HeroSection>
                                        <HeroTitle>{T._('Bring Zakeke to your customers', 'Admin')}</HeroTitle>
                                        <HeroDescription>{heroDescription}</HeroDescription>
                                    </HeroSection>

                                    <ChannelCardsGrid $singleColumn={!isStandaloneEnabled}>
                                        <ChannelCard>
                                            <CardBadge $variant='store'>{T._('Store', 'Admin')}</CardBadge>
                                            <CardTitle>{T._('Connect a Store', 'Admin')}</CardTitle>
                                            <CardText>
                                                {T._(
                                                    'Connect an online e-commerce or marketplace, B2C or B2B — Shopify, WooCommerce, Magento and more. Zakeke is embedded in your store as part of the buying experience.',
                                                    'Admin'
                                                )}
                                            </CardText>
                                            <FeatureList>
                                                <FeatureItem>{T._('Online store or marketplace, B2C or B2B', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Zakeke embedded in your storefront', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Part of the purchase experience', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Code-free connectors or API', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Automatic Product Sync', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Real-time Order Management', 'Admin')}</FeatureItem>
                                            </FeatureList>
                                            <CardAction>
                                                <FullWidthPrimaryButton $size='medium' onClick={() => handleAddSaleChannel()}>
                                                    {T._('Connect Store', 'Admin')}
                                                </FullWidthPrimaryButton>
                                            </CardAction>
                                        </ChannelCard>

                                        {isStandaloneEnabled && (
                                            <ChannelCard>
                                                <CardBadge $variant='portal'>{T._('Mini-Site', 'Admin')}</CardBadge>
                                                <CardTitle>{T._('Create a Mini-Site', 'Admin')}</CardTitle>
                                                <CardText>
                                                    {T._(
                                                        'Launch a standalone customizer portal hosted by us. Ideal for kiosks, showrooms, B2B sales, and proofing.',
                                                        'Admin'
                                                    )}
                                                </CardText>
                                                <FeatureList>
                                                    <FeatureItem>{T._('Ideal for kiosk & showroom experiences', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Great for B2B sales & proofing', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Customizable Theme', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Custom Sub-domain', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Products List & Collections', 'Admin')}</FeatureItem>
                                                </FeatureList>
                                                <CardAction>
                                                    <FullWidthPrimaryButton $size='medium' onClick={() => handleCreateMiniSite()}>
                                                        {T._('Create Mini-Site', 'Admin')}
                                                    </FullWidthPrimaryButton>
                                                </CardAction>
                                            </ChannelCard>
                                        )}
                                    </ChannelCardsGrid>

                                    {isStandaloneEnabled && (
                                        <InfoBanner>
                                            <InfoBannerContent>
                                                <InfoIcon>i</InfoIcon>
                                                <InfoBannerTextBlock>
                                                    <strong>{T._('Need help deciding?', 'Admin')}</strong>
                                                    <div>
                                                        {T._('Compare the features of stores versus standalone mini-sites in our documentation.', 'Admin')}
                                                    </div>
                                                </InfoBannerTextBlock>
                                            </InfoBannerContent>
                                            <TextButton onClick={openHelp}>{T._('Read Guide', 'Admin')}</TextButton>
                                        </InfoBanner>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </StyledContainerBase>
            )}
        </InlineHelpLayout>
    );
};

export default SalesChannels;
